import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { adminApi, type CreateCustomerResponse, type Plan } from '../../lib/adminApi.js';
import { toE164 } from '../../lib/phone.js';
import { Icon } from '../../components/Icon.js';
import { InviteLinkBox } from '../../components/InviteLinkBox.js';

// Create a customer. On success the platform provisions the tenant + a not-yet-activated owner and
// returns a one-time invite link, shown prominently for copy / SMS. The monthly price can be set here
// (optional — it's also editable later on the detail screen); it's persisted to the Subscription.

const label: React.CSSProperties = { fontSize: 13, color: 'var(--text-dim)', marginBottom: 8, fontWeight: 600, display: 'block' };
const input: React.CSSProperties = {
  width: '100%', height: 46, padding: '0 14px', borderRadius: 'var(--pill)', border: '1px solid var(--hair)',
  background: 'var(--surface-1)', color: 'var(--text)', fontFamily: 'var(--font-body)', fontSize: 15,
};

const PLANS: { value: Plan; label: string }[] = [
  { value: 'pilot', label: 'פיילוט' },
  { value: 'basic', label: 'בסיסי' },
  { value: 'pro', label: 'מקצועי' },
  { value: 'enterprise', label: 'ארגוני' },
];

export function NewCustomerPage(): JSX.Element {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [companyName, setCompanyName] = useState('');
  const [ownerName, setOwnerName] = useState('');
  const [ownerEmail, setOwnerEmail] = useState('');
  const [ownerPhone, setOwnerPhone] = useState('');
  const [plan, setPlan] = useState<Plan>('pilot');
  const [priceShekels, setPriceShekels] = useState('');
  const [sendInviteSms, setSendInviteSms] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<CreateCustomerResponse | null>(null);

  async function submit(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const res = await adminApi.createCustomer({
        companyName: companyName.trim(),
        ownerEmail: ownerEmail.trim().toLowerCase(),
        ownerPhone: toE164(ownerPhone),
        ownerName: ownerName.trim() || undefined,
        plan,
        ...(priceShekels.trim() ? { priceAgorot: Math.max(0, Math.round(Number(priceShekels) * 100)) } : {}),
        sendInviteSms,
      });
      await queryClient.invalidateQueries({ queryKey: ['admin', 'customers'] });
      setResult(res);
    } catch (err) {
      setError(
        String(err).includes('email_taken')
          ? 'האימייל הזה כבר משויך ללקוח קיים'
          : 'יצירת הלקוח נכשלה. בדוק את הפרטים ונסה שוב.',
      );
    } finally {
      setBusy(false);
    }
  }

  if (result) {
    return (
      <div className="content" style={{ maxWidth: 720 }}>
        <div className="page-head">
          <div>
            <div className="eyebrow">קונסולת ניהול</div>
            <h1>הלקוח נוצר</h1>
            <div className="sub">{result.tenant.name} — {result.owner.email}</div>
          </div>
        </div>

        <InviteLinkBox url={result.inviteUrl} expiresAt={result.inviteExpiresAt} smsSent={result.inviteSmsSent} />

        <div style={{ display: 'flex', gap: 12, marginTop: 22 }}>
          <button className="btn" onClick={() => navigate(`/admin/customers/${result.tenant.id}`)}>
            לכרטיס הלקוח
          </button>
          <button className="btn ghost" onClick={() => navigate('/admin/customers')}>
            חזרה לרשימה
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="content" style={{ maxWidth: 720 }}>
      <div className="page-head">
        <div>
          <div className="eyebrow">קונסולת ניהול</div>
          <h1>לקוח חדש</h1>
          <div className="sub">הזן את פרטי החברה והבעלים — הלקוח יקבל קישור לקביעת סיסמה</div>
        </div>
      </div>

      <form onSubmit={submit} className="card" style={{ padding: 28 }}>
        <label style={label} htmlFor="companyName">שם החברה</label>
        <input id="companyName" style={{ ...input, marginBottom: 18 }} value={companyName}
          onChange={(e) => setCompanyName(e.target.value)} required maxLength={200} />

        <div className="grid-2" style={{ marginBottom: 18 }}>
          <div>
            <label style={label} htmlFor="ownerName">שם הבעלים (לא חובה)</label>
            <input id="ownerName" style={input} value={ownerName} onChange={(e) => setOwnerName(e.target.value)} maxLength={120} />
          </div>
          <div>
            <label style={label} htmlFor="plan">תוכנית</label>
            <select id="plan" style={{ ...input, appearance: 'auto' }} value={plan} onChange={(e) => setPlan(e.target.value as Plan)}>
              {PLANS.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
            </select>
          </div>
        </div>

        <div className="grid-2" style={{ marginBottom: 18 }}>
          <div>
            <label style={label} htmlFor="price">מחיר חודשי (₪, לא חובה — ניתן לקבוע גם אחר כך)</label>
            <input id="price" style={input} type="number" min={0} step={1} dir="ltr" placeholder="299"
              value={priceShekels} onChange={(e) => setPriceShekels(e.target.value)} />
          </div>
        </div>

        <div className="grid-2" style={{ marginBottom: 18 }}>
          <div>
            <label style={label} htmlFor="ownerEmail">אימייל הבעלים</label>
            <input id="ownerEmail" style={input} type="email" dir="ltr" value={ownerEmail}
              onChange={(e) => setOwnerEmail(e.target.value)} required maxLength={200} />
          </div>
          <div>
            <label style={label} htmlFor="ownerPhone">טלפון הבעלים</label>
            <input id="ownerPhone" style={input} type="tel" dir="ltr" placeholder="0541234567" value={ownerPhone}
              onChange={(e) => setOwnerPhone(e.target.value)} required />
          </div>
        </div>

        <label style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 14, cursor: 'pointer', marginBottom: 24 }}>
          <input type="checkbox" checked={sendInviteSms} onChange={(e) => setSendInviteSms(e.target.checked)} />
          שלח את קישור ההפעלה ללקוח ב-SMS
        </label>

        {error && (
          <div style={{ marginBottom: 18, padding: '10px 14px', borderRadius: 'var(--r-sm)', background: 'color-mix(in srgb, var(--risk) 12%, transparent)', color: 'var(--risk)', fontSize: 14 }}>
            {error}
          </div>
        )}

        <div style={{ display: 'flex', gap: 12 }}>
          <button className="btn" type="submit" disabled={busy}>
            <Icon name="plus" /> {busy ? 'יוצר…' : 'צור לקוח'}
          </button>
          <button className="btn ghost" type="button" onClick={() => navigate('/admin/customers')}>ביטול</button>
        </div>
      </form>
    </div>
  );
}
